// components/GedcomMergeModal.jsx
// Full-screen merge modal for matching GEDCOM imports to existing AI-extracted members.

import React from 'react';
import axios from 'axios';
import { Check, ChevronDown, ChevronRight, GitMerge, Loader, UserPlus, X, XCircle } from 'lucide-react';
import { useToast } from '../toastContext';
import Button from './ui/Button';
import { Z } from '../constants/tokens';

const CONFIDENCE_COLORS = {
  high: '#16a34a',
  medium: '#f59e0b',
  low: '#ef4444',
  none: '#6b7280',
};

export default function GedcomMergeModal({ backendUrl, root, gedcomText, theme, onClose, onComplete, existingSession }) {
  const { pushToast } = useToast();
  const [loading, setLoading] = React.useState(true);
  const [applying, setApplying] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [decisions, setDecisions] = React.useState({}); // gedcom_id → {action, db_id}
  const [expandedIdx, setExpandedIdx] = React.useState(null);
  const [sessionId, setSessionId] = React.useState(existingSession?.id || null);
  const saveTimerRef = React.useRef(null);

  // Load preview on mount
  React.useEffect(() => {
    setLoading(true);
    const tok = sessionStorage.getItem('gwv_token');
    const headers = tok ? { Authorization: `Bearer ${tok}` } : {};

    if (existingSession) {
      // Resume from saved session
      setPreview(existingSession.preview_data);
      setDecisions(existingSession.decisions || {});
      setSessionId(existingSession.id);
      setLoading(false);
    } else if (gedcomText) {
      // Fresh GEDCOM — create a session
      axios.post(
        `${backendUrl}/subject/family/merge-session?root=${encodeURIComponent(root)}`,
        { gedcom: gedcomText },
        { headers }
      )
        .then(r => {
          setSessionId(r.data.session_id);
          // Now load the full session data
          return axios.get(`${backendUrl}/subject/family/merge-session?root=${encodeURIComponent(root)}`, { headers });
        })
        .then(r => {
          const session = r.data.session;
          if (session) {
            setPreview(session.preview_data);
            setDecisions(session.decisions || {});
          }
        })
        .catch(err => setError(err.response?.data?.detail || err.message))
        .finally(() => setLoading(false));
    }
  }, [backendUrl, root, gedcomText, existingSession]);

  // Auto-save decisions to server (debounced)
  React.useEffect(() => {
    if (!sessionId || loading) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const tok = sessionStorage.getItem('gwv_token');
      axios.put(
        `${backendUrl}/subject/family/merge-session/${sessionId}`,
        { decisions },
        tok ? { headers: { Authorization: `Bearer ${tok}` } } : {}
      ).catch(() => {}); // silent save
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [decisions, sessionId, backendUrl, loading]);

  const applyMerge = () => {
    setApplying(true);
    const merges = (preview?.matches || []).map(m => {
      const gid = m.gedcom.gedcom_id;
      const dec = decisions[gid] || { action: 'skip', db_id: null };
      return {
        gedcom: m.gedcom,
        db_id: dec.db_id,
        action: dec.action,
      };
    });

    const tok = sessionStorage.getItem('gwv_token');
    axios.post(
      `${backendUrl}/subject/family/apply-merge?root=${encodeURIComponent(root)}`,
      { merges, gedcom_families: preview?.gedcom_families || [], session_id: sessionId },
      tok ? { headers: { Authorization: `Bearer ${tok}` } } : {}
    )
      .then(r => {
        const d = r.data;
        // Pushed before onComplete() — that unmounts this modal, and the toast
        // lives in App's state so it outlives us and is actually seen.
        pushToast('gedcom-merge', 'success', `Merge complete: ${d.merged} merged, ${d.inserted} new, ${d.skipped} skipped, ${d.relationships_created} relationships created.`);
        onComplete();
      })
      .catch(err => pushToast('gedcom-merge', 'error', 'Merge failed: ' + (err.response?.data?.detail || err.message)))
      .finally(() => setApplying(false));
  };

  const setAction = (gedcomId, action, dbId = null) => {
    setDecisions(prev => ({ ...prev, [gedcomId]: { action, db_id: dbId } }));
  };

  // Stats
  const stats = preview?.stats || {};
  const mergeCount = Object.values(decisions).filter(d => d.action === 'merge').length;
  const newCount = Object.values(decisions).filter(d => d.action === 'gedcom_only').length;
  const skipCount = Object.values(decisions).filter(d => d.action === 'skip').length;

  const fieldRow = (label, gedcomVal, dbVal) => {
    const gv = gedcomVal || '';
    const dv = dbVal || '';
    const same = gv.toLowerCase().trim() === dv.toLowerCase().trim();
    if (!gv && !dv) return null;
    return (
      <div key={label} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '6px', fontSize: '0.64rem', padding: '2px 0' }}>
        <span style={{ color: theme.textSub, fontWeight: 700 }}>{label}</span>
        <span dir="auto" style={{ color: gv ? (same ? theme.text : '#16a34a') : theme.textSub, fontWeight: gv && !same ? 700 : 400 }}>
          {gv || '—'}
        </span>
        <span dir="auto" style={{ color: dv ? (same ? theme.text : '#f59e0b') : theme.textSub, fontWeight: dv && !same ? 700 : 400 }}>
          {dv || '—'}
        </span>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: Z.modal,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '900px', maxHeight: '90vh',
        background: theme.bg || theme.card, border: `1px solid ${theme.border}`,
        borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: theme.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GitMerge size={18} strokeWidth={2} />
                GEDCOM Merge
              </div>
              <div style={{ fontSize: '0.68rem', color: theme.textSub, marginTop: '4px' }}>
                Match imported GEDCOM members to existing AI-extracted data. GEDCOM is the genealogical source of truth; AI data enriches it with testimony context.
              </div>
            </div>
            <Button theme={theme} variant="outline" size="sm" icon={X} onClick={onClose} aria-label="Close" />
          </div>

          {/* Stats bar */}
          {preview && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
              {[
                { label: 'GEDCOM', value: stats.gedcom_total, color: '#0f766e' },
                { label: 'Existing', value: stats.db_total, color: '#2563eb' },
                { label: 'Auto-matched', value: stats.auto_matched, color: '#16a34a' },
                { label: 'To merge', value: mergeCount, color: '#16a34a' },
                { label: 'New', value: newCount, color: '#f59e0b' },
                { label: 'Skip', value: skipCount, color: '#6b7280' },
              ].map(s => (
                <span key={s.label} style={{ fontSize: '0.58rem', fontWeight: 700, padding: '3px 8px', borderRadius: '10px', color: s.color, background: `${s.color}14`, border: `1px solid ${s.color}33` }}>
                  {s.value} {s.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: theme.textSub }}>
              <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <div style={{ marginTop: '10px', fontSize: '0.78rem' }}>Analyzing GEDCOM and matching members...</div>
            </div>
          )}

          {error && (
            <div style={{ padding: '20px', color: '#ef4444', textAlign: 'center' }}>
              <XCircle size={24} />
              <div style={{ marginTop: '8px' }}>{error}</div>
            </div>
          )}

          {preview && !loading && (
            <>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 50px 1fr 100px', gap: '8px', padding: '6px 8px', fontSize: '0.56rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${theme.border}`, marginBottom: '6px' }}>
                <span></span>
                <span>GEDCOM (Source)</span>
                <span style={{ textAlign: 'center' }}>Match</span>
                <span>Existing (AI/Manual)</span>
                <span>Action</span>
              </div>

              {(preview.matches || []).map((match, idx) => {
                const gm = match.gedcom;
                const dm = match.db_match;
                const gid = gm.gedcom_id;
                const dec = decisions[gid] || { action: 'skip' };
                const conf = match.confidence;
                const isExpanded = expandedIdx === idx;

                return (
                  <div key={gid} style={{
                    border: `1px solid ${conf === 'high' ? '#16a34a33' : conf === 'medium' ? '#f59e0b33' : theme.border}`,
                    borderRadius: '10px', marginBottom: '6px', overflow: 'hidden',
                    background: dec.action === 'skip' ? `${theme.border}22` : theme.card,
                  }}>
                    {/* Summary row */}
                    <div
                      onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                      style={{ display: 'grid', gridTemplateColumns: '28px 1fr 50px 1fr 100px', gap: '8px', padding: '8px', cursor: 'pointer', alignItems: 'center' }}
                    >
                      <Button
                        theme={theme}
                        variant="ghost"
                        size="sm"
                        icon={isExpanded ? ChevronDown : ChevronRight}
                        onClick={e => { e.stopPropagation(); setExpandedIdx(isExpanded ? null : idx); }}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${gm.name}`}
                        style={{ width: '28px', height: '28px', minHeight: '28px', padding: 0 }}
                      />

                      {/* GEDCOM name */}
                      <div>
                        <div dir="auto" style={{ fontSize: '0.76rem', fontWeight: 700, color: theme.text }}>{gm.name}</div>
                        <div style={{ fontSize: '0.58rem', color: theme.textSub }}>
                          {gm.birth_year || '?'}{gm.death_year ? ` – ${gm.death_year}` : ''}
                          {gm.birth_place ? ` · ${gm.birth_place}` : ''}
                        </div>
                      </div>

                      {/* Confidence badge */}
                      <div style={{ textAlign: 'center' }}>
                        {dm ? (
                          <span style={{ fontSize: '0.5rem', fontWeight: 700, padding: '2px 6px', borderRadius: '10px',
                            color: CONFIDENCE_COLORS[conf], background: `${CONFIDENCE_COLORS[conf]}14`, border: `1px solid ${CONFIDENCE_COLORS[conf]}33` }}>
                            {conf.toUpperCase()}{match.score ? ` ${match.score}` : ''}
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.5rem', fontWeight: 700, padding: '2px 6px', borderRadius: '10px', color: '#6b7280', background: '#6b728014', border: '1px solid #6b728033' }}>NEW</span>
                        )}
                      </div>

                      {/* DB match */}
                      <div>
                        {dm ? (
                          <>
                            <div dir="auto" style={{ fontSize: '0.76rem', fontWeight: 700, color: theme.text }}>{dm.name}</div>
                            <div style={{ fontSize: '0.58rem', color: theme.textSub }}>
                              {dm.birth_year || '?'}{dm.death_year ? ` – ${dm.death_year}` : ''}
                              {dm.birth_place ? ` · ${dm.birth_place}` : ''}
                              <span style={{ marginLeft: '4px', fontSize: '0.48rem', fontWeight: 700, padding: '0px 4px', borderRadius: '6px',
                                color: dm.source === 'ai' ? theme.textSub : dm.source === 'gedcom' ? '#0f766e' : '#007aff',
                                background: dm.source === 'ai' ? `${theme.border}44` : dm.source === 'gedcom' ? '#0f766e14' : '#007aff14',
                              }}>{(dm.source || 'ai').toUpperCase()}</span>
                            </div>
                          </>
                        ) : (
                          <div style={{ fontSize: '0.68rem', color: theme.textSub, fontStyle: 'italic' }}>No match found</div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                        {dm && (
                          <button
                            onClick={() => setAction(gid, 'merge', dm.id)}
                            title="Merge: GEDCOM data + AI enrichment"
                            style={{ padding: '3px 6px', fontSize: '0.52rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer',
                              color: dec.action === 'merge' ? '#fff' : '#16a34a',
                              background: dec.action === 'merge' ? '#16a34a' : 'transparent',
                              border: `1px solid #16a34a${dec.action === 'merge' ? '' : '44'}` }}
                          >
                            <GitMerge size={9} style={{ verticalAlign: 'middle' }} /> Merge
                          </button>
                        )}
                        <button
                          onClick={() => setAction(gid, 'gedcom_only', null)}
                          title="Import as new GEDCOM member"
                          style={{ padding: '3px 6px', fontSize: '0.52rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer',
                            color: dec.action === 'gedcom_only' ? '#fff' : '#f59e0b',
                            background: dec.action === 'gedcom_only' ? '#f59e0b' : 'transparent',
                            border: `1px solid #f59e0b${dec.action === 'gedcom_only' ? '' : '44'}` }}
                        >
                          <UserPlus size={9} style={{ verticalAlign: 'middle' }} /> New
                        </button>
                        <button
                          onClick={() => setAction(gid, 'skip', dm?.id || null)}
                          title="Skip — don't import this person"
                          style={{ padding: '3px 6px', fontSize: '0.52rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer',
                            color: dec.action === 'skip' ? '#fff' : '#6b7280',
                            background: dec.action === 'skip' ? '#6b7280' : 'transparent',
                            border: `1px solid #6b7280${dec.action === 'skip' ? '' : '44'}` }}
                        >
                          Skip
                        </button>
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={{ padding: '0 8px 10px 36px', borderTop: `1px solid ${theme.border}` }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '6px', fontSize: '0.56rem', fontWeight: 700, color: theme.textSub, padding: '6px 0 2px', textTransform: 'uppercase' }}>
                          <span>Field</span>
                          <span style={{ color: '#0f766e' }}>GEDCOM</span>
                          <span style={{ color: '#2563eb' }}>Existing</span>
                        </div>
                        {fieldRow('Name', gm.name, dm?.name)}
                        {fieldRow('Sex', gm.sex, dm?.sex)}
                        {fieldRow('Born', gm.birth_year, dm?.birth_year)}
                        {fieldRow('Died', gm.death_year, dm?.death_year)}
                        {fieldRow('Birth place', gm.birth_place, dm?.birth_place)}
                        {fieldRow('Death place', gm.death_place, dm?.death_place)}
                        {fieldRow('Married name', gm.married_name, dm?.married_name)}
                        {fieldRow('Relation', '', dm?.relation)}
                        {fieldRow('Fate', '', dm?.fate)}
                        {fieldRow('Fate detail', '', dm?.fate_detail)}
                        {fieldRow('Notes', gm.notes, dm?.notes)}
                        {fieldRow('Source ref', '', dm?.source_locator)}
                        {match.match_reason && (
                          <div style={{ fontSize: '0.58rem', color: CONFIDENCE_COLORS[conf], marginTop: '6px', fontWeight: 700 }}>
                            Match reason: {match.match_reason}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Unmatched DB members (AI members with no GEDCOM counterpart) */}
              {(preview.unmatched_db_members || []).length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                    Existing members with no GEDCOM match ({preview.unmatched_db_members.length})
                  </div>
                  <div style={{ fontSize: '0.6rem', color: theme.textSub, marginBottom: '8px' }}>
                    These AI/manual members were not matched to any GEDCOM person. They will be kept as-is.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {[...preview.unmatched_db_members].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(m => (
                      <span key={m.id} dir="auto" style={{ padding: '3px 8px', fontSize: '0.6rem', fontWeight: 600, borderRadius: '8px', border: `1px solid ${theme.border}`, color: theme.textSub }}>
                        {m.name} {m.birth_year ? `(${m.birth_year})` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${theme.border}`, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.64rem', color: theme.textSub }}>
            Merge rules: GEDCOM wins for names, dates, places. AI wins for fate, notes, transcript references.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button theme={theme} variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              theme={theme}
              variant="success"
              icon={Check}
              loading={applying}
              disabled={applying || loading}
              onClick={applyMerge}
            >
              Apply Merge ({mergeCount} merge, {newCount} new)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
